import type { JSONSchemaType } from 'ajv'
import type { ActionDefinition, ActionOutcome } from '../action'
import { getAction, registerAction } from '../registry'
import { setHelperAppAccess } from '../../helperAppAccessService'

export const MEMBER_INVITE_ACTION_ID = 'member.invite.create'
export const MEMBER_SET_HELPER_APP_ACCESS_ACTION_ID = 'member.helper_app_access.set'
export const MEMBER_SET_HELPER_NOTIFY_VIA_ACTION_ID = 'member.helper_notify_via.set'

type MemberInviteArgs = { prompt: string }
export type MemberInviteConfirmPayload = { kind: 'MEMBER_INVITE'; email: string | null; phone: string | null; role: string }

const memberInviteArgsSchema: JSONSchemaType<MemberInviteArgs> = {
  type: 'object', properties: { prompt: { type: 'string' } }, required: ['prompt'], additionalProperties: false,
}

const EMAIL_PATTERN = /\b([\w.+-]+@[\w-]+\.[\w.-]+)\b/i
const PHONE_PATTERN = /\b(\+?\d[\d\s-]{7,}\d)\b/
const ROLE_PATTERN = /\bas\s+(?:an?\s+)?(helper|admin|member)\b/i

const memberInviteAction: ActionDefinition<MemberInviteArgs> = {
  id: MEMBER_INVITE_ACTION_ID,
  label: 'Invite member',
  description: 'Prepares an email or phone membership invitation for confirmation.',
  risk: 'HIGH',
  domain: 'member',
  surfaces: ['ai', 'catalog'],
  argsSchema: memberInviteArgsSchema,
  permission: (context) => context.canPerform('workspace.manage_invites') || context.canPerform('workspace.manage_field_passes'),
  run: async ({ prompt }, context): Promise<ActionOutcome> => {
    const email = prompt.match(EMAIL_PATTERN)?.[1] ?? null
    const phone = email ? null : prompt.match(PHONE_PATTERN)?.[1] ?? null
    const role = prompt.match(ROLE_PATTERN)?.[1]?.toLowerCase() ?? 'member'
    if (!email && !phone) {
      return { kind: 'FAILED', message: 'Provide an email or phone for the person you want to invite.', recoverable: true, correlationId: context.correlationId! }
    }
    const contactLabel = email ?? phone!
    const confirmPayload: MemberInviteConfirmPayload = { kind: 'MEMBER_INVITE', email, phone, role }
    return {
      kind: 'NEEDS_CONFIRMATION',
      message: `Invite ${contactLabel} as ${role}. Confirm?`,
      confirmPayload,
      correlationId: context.correlationId!,
    }
  },
}

type MemberSetHelperAppAccessArgs = {
  workspaceId: string
  uid: string
  appAccess: boolean
}

const memberSetHelperAppAccessArgsSchema: JSONSchemaType<MemberSetHelperAppAccessArgs> = {
  type: 'object',
  properties: {
    workspaceId: { type: 'string' },
    uid: { type: 'string' },
    appAccess: { type: 'boolean' },
  },
  required: ['workspaceId', 'uid', 'appAccess'],
  additionalProperties: false,
}

const memberSetHelperAppAccessAction: ActionDefinition<MemberSetHelperAppAccessArgs> = {
  id: MEMBER_SET_HELPER_APP_ACCESS_ACTION_ID,
  label: 'Set helper app access',
  description: 'Enables or disables Helper app sign-in and access for a helper.',
  risk: 'MEDIUM',
  domain: 'member',
  surfaces: ['ai', 'catalog'],
  argsSchema: memberSetHelperAppAccessArgsSchema,
  permission: (context) => context.canPerform('workspace.manage_invites'),
  run: async (args, context): Promise<ActionOutcome> => {
    try {
      const res = await setHelperAppAccess(args)
      return {
        kind: 'COMPLETED',
        result: res,
        message: `Helper app access set to ${res.appAccess}.`,
        correlationId: context.correlationId!,
      }
    } catch (err: unknown) {
      return {
        kind: 'FAILED',
        message: err instanceof Error ? err.message : 'Failed to set helper app access.',
        recoverable: true,
        correlationId: context.correlationId!,
      }
    }
  },
}

type MemberSetHelperNotifyViaArgs = {
  workspaceId: string
  uid?: string
  notifyVia: 'BOTH' | 'APP_ONLY' | 'WHATSAPP_ONLY'
}

const memberSetHelperNotifyViaArgsSchema: JSONSchemaType<MemberSetHelperNotifyViaArgs> = {
  type: 'object',
  properties: {
    workspaceId: { type: 'string' },
    uid: { type: 'string', nullable: true },
    notifyVia: { type: 'string', enum: ['BOTH', 'APP_ONLY', 'WHATSAPP_ONLY'] },
  },
  required: ['workspaceId', 'notifyVia'],
  additionalProperties: false,
}

const memberSetHelperNotifyViaAction: ActionDefinition<MemberSetHelperNotifyViaArgs> = {
  id: MEMBER_SET_HELPER_NOTIFY_VIA_ACTION_ID,
  label: 'Set helper notification routing',
  description: 'Sets notification routing (BOTH, APP_ONLY, WHATSAPP_ONLY) for a helper.',
  risk: 'MEDIUM',
  domain: 'member',
  surfaces: ['ai', 'catalog'],
  argsSchema: memberSetHelperNotifyViaArgsSchema,
  permission: () => true,
  run: async (args, context): Promise<ActionOutcome> => {
    try {
      const { setHelperNotifyVia } = await import('../../helperNotifyViaService')
      const res = await setHelperNotifyVia(args)
      return {
        kind: 'COMPLETED',
        result: res,
        message: `Helper notification routing set to ${res.notifyVia}.`,
        correlationId: context.correlationId!,
      }
    } catch (err: unknown) {
      return {
        kind: 'FAILED',
        message: err instanceof Error ? err.message : 'Failed to set helper notification routing.',
        recoverable: true,
        correlationId: context.correlationId!,
      }
    }
  },
}

export const registerMemberInviteAction = (): void => {
  if (!getAction(MEMBER_INVITE_ACTION_ID)) registerAction(memberInviteAction)
  if (!getAction(MEMBER_SET_HELPER_APP_ACCESS_ACTION_ID)) registerAction(memberSetHelperAppAccessAction)
  if (!getAction(MEMBER_SET_HELPER_NOTIFY_VIA_ACTION_ID)) registerAction(memberSetHelperNotifyViaAction)
}

registerMemberInviteAction()
